import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import {
  Truck, X, Plus, Tag, Clock, Shield, Wallet, Video, Trash2,
  CheckCircle, Package, FolderPlus, Search, Send, ChevronLeft,
  Youtube, ChevronUp, ChevronDown
} from 'lucide-react';
import { formatPrice } from '../lib/currency';
import { useSiteSettings } from '../hooks/useSiteSettings';
import ProductCard, { Product, Category } from '../components/ProductCard';
import ProductEditForm, { LeasingCategoryInfo } from '../components/ProductEditForm';
import ProductImageGallery from '../components/ProductImageGallery';
import ParticleBackground from '../components/ParticleBackground';
import {
  getProductsLight, getCategories, convertToLegacyProduct, convertLegacyProduct,
  updateProduct, clearProductCache,
  getLeasingCategories, createLeasingCategory, deleteLeasingCategory,
  getProductLeasingAssignments, assignProductToLeasingCategory,
  unassignProductFromLeasingCategory, assignProductsToLeasingCategory,
  type LeasingCategory,
} from '../lib/supabase';

interface LeasingProduct extends Product {
  leasingCategoryIds: string[];
}

export default function OperativniLeasingPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { settings, updateSetting } = useSiteSettings();

  const leasingVideoUrl = settings.leasing_video_tutorial_url ?? '';
  const leasingVideoId = extractYouTubeId(leasingVideoUrl);
  const [showLeasingVideo, setShowLeasingVideo] = useState(false);
  const [showLeasingVideoModal, setShowLeasingVideoModal] = useState(false);
  const [allRentalProducts, setAllRentalProducts] = useState<LeasingProduct[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [leasingCategories, setLeasingCategories] = useState<LeasingCategory[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<LeasingProduct | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Admin states
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#EE1C23');
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [addProductSearch, setAddProductSearch] = useState('');
  const [allProductsForAdd, setAllProductsForAdd] = useState<Product[]>([]);
  const [selectedLeasingCatForAdd, setSelectedLeasingCatForAdd] = useState<string>('');
  const [selectedProductIdsForAdd, setSelectedProductIdsForAdd] = useState<Set<string>>(new Set());

  // Rental request states (inline within product details modal)
  const [showRentalFormInline, setShowRentalFormInline] = useState(false);
  const [rentalForm, setRentalForm] = useState({
    name: '', email: '', phone: '', notes: '', gdprConsent: false
  });
  const [rentalSubmitted, setRentalSubmitted] = useState(false);
  const [rentalSubmitting, setRentalSubmitting] = useState(false);
  const [rentalError, setRentalError] = useState(false);

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' ||
                  localStorage.getItem('admin_logged_in') === 'true';

  const currentLang = (i18n.language === 'cz' ? 'cs' : i18n.language) as 'cs' | 'en' | 'de' | 'sk';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [allProducts, leasingCats, assignmentData] = await Promise.all([
        getProductsLight(undefined, true, true),
        getLeasingCategories(),
        getProductLeasingAssignments(),
      ]);

      const rentalProducts = (allProducts as any[])
        .map((p: any) => {
          const legacy = convertToLegacyProduct(p) as Product;
          return { ...legacy, leasingCategoryIds: assignmentData[p.id] || [] } as LeasingProduct;
        });

      setAllRentalProducts(rentalProducts);
      setLeasingCategories(leasingCats);
      setAssignments(assignmentData);

      const sections = ['unlimited', 'robotics', 'sex-machines'] as const;
      const catResults = await Promise.all(sections.map(s => getCategories(s)));
      const mergedCategories: Category[] = [];
      catResults.forEach((cats: any[]) => {
        cats.forEach((cat: any) => {
          if (!mergedCategories.find(c => c.id === cat.id)) {
            mergedCategories.push({ id: cat.id, name: cat.name, color: cat.color });
          }
        });
      });
      setAllCategories(mergedCategories);
    } catch (err: any) {
      console.error('Error loading leasing data:', err);
      setError(err.message || t('leasing.errorLoadingProducts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    if (!showProductDetails && !showEditModal && !showAddCategoryModal && !showAddProductModal) {
      document.body.style.overflow = 'unset';
    }
  }, [showProductDetails, showEditModal, showAddCategoryModal, showAddProductModal]);

  // ── Admin: category management ──────────────────────────────────
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await createLeasingCategory(newCategoryName.trim(), newCategoryColor);
      setNewCategoryName('');
      setNewCategoryColor('#EE1C23');
      setShowAddCategoryModal(false);
      document.body.style.overflow = 'unset';
      window.dispatchEvent(new Event('modalClose'));
      await loadData();
    } catch (err) {
      console.error('Error creating leasing category:', err);
      alert(t('leasing.errorCreatingCategory'));
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm(t('leasing.confirmDeleteCategory'))) return;
    try {
      await deleteLeasingCategory(id);
      if (selectedCategory === id) setSelectedCategory('all');
      await loadData();
    } catch (err) {
      console.error('Error deleting leasing category:', err);
      alert('Chyba při mazání kategorie');
    }
  };

  // ── Admin: add products to leasing ───────────────────────────────
  const handleOpenAddProduct = async () => {
    try {
      const allProducts = await getProductsLight(undefined, true, false);
      const legacyProducts = (allProducts as any[]).map(convertToLegacyProduct) as Product[];
      setAllProductsForAdd(legacyProducts);
      setSelectedLeasingCatForAdd(leasingCategories.length > 0 ? leasingCategories[0].id : '');
      setSelectedProductIdsForAdd(new Set());
      setAddProductSearch('');
      setShowAddProductModal(true);
      document.body.style.overflow = 'hidden';
      window.dispatchEvent(new Event('modalOpen'));
    } catch (err) {
      console.error('Error loading products for add:', err);
      alert(t('leasing.errorLoadingProducts'));
    }
  };

  const handleConfirmAddProducts = async () => {
    if (selectedProductIdsForAdd.size === 0) {
      alert(t('leasing.selectAtLeastOne'));
      return;
    }
    try {
      const productIds = Array.from(selectedProductIdsForAdd);
      await Promise.all(productIds.map(async (pid) => {
        await updateProduct(pid, { is_rental: true } as any);
      }));

      if (selectedLeasingCatForAdd) {
        await assignProductsToLeasingCategory(productIds, selectedLeasingCatForAdd);
      }

      clearProductCache();
      setShowAddProductModal(false);
      setSelectedProductIdsForAdd(new Set());
      document.body.style.overflow = 'unset';
      window.dispatchEvent(new Event('modalClose'));
      await loadData();
    } catch (err) {
      console.error('Error adding products to leasing:', err);
      alert(t('leasing.errorAddingProducts'));
    }
  };

  const handleRemoveFromLeasing = async (productId: string) => {
    if (!confirm(t('leasing.confirmRemoveFromLeasing'))) return;
    try {
      await updateProduct(productId, { is_rental: false } as any);
      clearProductCache();
      await loadData();
    } catch (err) {
      console.error('Error removing from leasing:', err);
      alert(t('leasing.errorRemovingProduct'));
    }
  };

  const handleAssignToCategory = async (productId: string, categoryId: string) => {
    try {
      const current = assignments[productId] || [];
      if (current.includes(categoryId)) {
        await unassignProductFromLeasingCategory(productId, categoryId);
      } else {
        await assignProductToLeasingCategory(productId, categoryId);
      }
      await loadData();
    } catch (err) {
      console.error('Error assigning category:', err);
    }
  };

  // ── Product edit ─────────────────────────────────────────────────
  const handleEditProduct = async (updatedProduct: Product, newLeasingCatIds?: string[]) => {
    try {
      const supabaseData = convertLegacyProduct(updatedProduct);
      await updateProduct(updatedProduct.id, supabaseData);
      clearProductCache();

      if (newLeasingCatIds !== undefined) {
        const oldCatIds = assignments[updatedProduct.id] || [];
        const toAdd = newLeasingCatIds.filter(id => !oldCatIds.includes(id));
        const toRemove = oldCatIds.filter(id => !newLeasingCatIds.includes(id));
        await Promise.all([
          ...toAdd.map(id => assignProductToLeasingCategory(updatedProduct.id, id)),
          ...toRemove.map(id => unassignProductFromLeasingCategory(updatedProduct.id, id)),
        ]);
      }

      await loadData();
    } catch (err) {
      console.error('Error updating product:', err);
    }
    setShowEditModal(false);
    setEditingProduct(null);
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const handleShowDetails = (product: LeasingProduct) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const handleEditClick = (product: Product) => {
    setEditingProduct(product);
    setShowEditModal(true);
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const handleRentalRequest = (product: Product) => {
    setSelectedProduct(product as LeasingProduct);
    setShowProductDetails(true);
    setShowRentalFormInline(true);
    setRentalSubmitted(false);
    setRentalForm({ name: '', email: '', phone: '', notes: '', gdprConsent: false });
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const handleSubmitRental = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct) return;
    if (!rentalForm.gdprConsent) return;
    setRentalSubmitting(true);
    setRentalError(false);
    try {
      emailjs.init('saGOmdFFdZT_Ravmd');
      const productName = selectedProduct.name[currentLang] || selectedProduct.name.cs;
      const messageParts = [
        `${t('leasing.machine')}: ${productName}`,
        `${t('leasing.monthlyFlat')}: ${formatPrice(selectedProduct.rentalPriceMonthly || 0, currentLang)}`,
        `${t('leasing.insurancePerMonth')}: ${formatPrice(selectedProduct.rentalInsuranceMonthly || 0, currentLang)}`,
        `${t('leasing.operatingHour')}: ${formatPrice(selectedProduct.rentalHourlyRate || 0, currentLang)}`,
        rentalForm.notes ? `${t('leasing.notes')}: ${rentalForm.notes}` : '',
      ].filter(Boolean).join('\n');
      await emailjs.send(
        'service_vxop1mt',
        'template_rzipdon',
        {
          from_name: rentalForm.name,
          reply_to: rentalForm.email,
          phone: rentalForm.phone || 'Neuvedeno',
          message: messageParts,
          email: 'kontakt@digitalshrooms.cz'
        }
      );
      setRentalSubmitted(true);
    } catch (err) {
      console.error('Error submitting rental request:', err);
      setRentalError(true);
    } finally {
      setRentalSubmitting(false);
    }
  };

  const closeProductDetails = () => {
    setShowProductDetails(false);
    setShowRentalFormInline(false);
    setRentalSubmitted(false);
    setRentalError(false);
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const backToProductDetails = () => {
    setShowRentalFormInline(false);
    setRentalSubmitted(false);
    setRentalError(false);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
  };

  // ── Derived data ─────────────────────────────────────────────────
  const productsByLeasingCategory = new Map<string, LeasingProduct[]>();
  allRentalProducts.forEach(product => {
    if (product.leasingCategoryIds && product.leasingCategoryIds.length > 0) {
      product.leasingCategoryIds.forEach(catId => {
        const existing = productsByLeasingCategory.get(catId) || [];
        productsByLeasingCategory.set(catId, [...existing, product]);
      });
    } else {
      const uncategorized = productsByLeasingCategory.get('uncategorized') || [];
      productsByLeasingCategory.set('uncategorized', [...uncategorized, product]);
    }
  });

  const filteredProducts = selectedCategory === 'all'
    ? allRentalProducts
    : (productsByLeasingCategory.get(selectedCategory) || []);

  const filteredAddProducts = allProductsForAdd.filter(p => {
    const name = p.name[currentLang] || p.name.cs || '';
    return name.toLowerCase().includes(addProductSearch.toLowerCase());
  });

  const leasingFeatures = [
    {
      icon: <Wallet className="w-10 h-10" />,
      title: t('leasing.featureMonthlyTitle'),
      description: t('leasing.featureMonthlyDesc'),
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: t('leasing.featureInsuranceTitle'),
      description: t('leasing.featureInsuranceDesc'),
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: t('leasing.featureHoursTitle'),
      description: t('leasing.featureHoursDesc'),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />

      {/* Hero */}
      <section className="relative py-20 px-4 z-10">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center justify-center mb-6">
              <Truck className="w-16 h-16 text-[#EE1C23] mr-4" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                {t('leasing.title')}
              </h1>
            </div>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t('leasing.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Video tutorial */}
      {leasingVideoId && (
        <section className="py-4 px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            <LeasingVideoTutorialSection
              videoId={leasingVideoId}
              expanded={showLeasingVideo}
              onToggle={() => setShowLeasingVideo(!showLeasingVideo)}
            />
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-12 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {leasingFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-8 text-center hover:border-red-400/40 transition-all hover:scale-105"
              >
                <div className="text-[#EE1C23] mb-6 flex justify-center">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-4 font-orbitron">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin toolbar */}
      {isAdmin && (
        <section className="py-4 px-4 relative z-10">
          <div className="max-w-7xl mx-auto flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => {
                setShowAddCategoryModal(true);
                document.body.style.overflow = 'hidden';
                window.dispatchEvent(new Event('modalOpen'));
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors font-medium"
            >
              <FolderPlus className="w-4 h-4" />
              <span>{t('leasing.addCategory')}</span>
            </button>
            <button
              onClick={handleOpenAddProduct}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              <span>{t('leasing.addMachineToLeasing')}</span>
            </button>
            <button
              onClick={() => setShowLeasingVideoModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors font-medium border border-white/20"
            >
              <Video className="w-4 h-4" />
              <span>{t('leasing.setVideoTutorial')}</span>
            </button>
          </div>
        </section>
      )}

      {/* Admin video tutorial modal */}
      {showLeasingVideoModal && (
        <LeasingVideoTutorialModal
          currentUrl={leasingVideoUrl}
          onClose={() => setShowLeasingVideoModal(false)}
          onSave={async (url) => {
            await updateSetting('leasing_video_tutorial_url', url);
            setShowLeasingVideoModal(false);
          }}
          onRemove={async () => {
            await updateSetting('leasing_video_tutorial_url', '');
            setShowLeasingVideoModal(false);
          }}
        />
      )}

      {/* Products */}
      <section className="py-16 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <Tag className="w-12 h-12 text-[#EE1C23] mr-4" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                {t('leasing.availableMachines')}
              </h2>
            </div>
          </motion.div>

          {loading && (
            <div className="text-center py-20">
              <div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">{t('leasing.loadingMachines')}</p>
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <p className="text-red-400 mb-4">{error}</p>
              <button onClick={loadData} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors">
                {t('leasing.tryAgain')}
              </button>
            </div>
          )}

          {!loading && !error && allRentalProducts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">{t('leasing.noMachines')}</p>
              {isAdmin && (
                <p className="text-gray-500 text-sm mt-2">{t('leasing.noMachinesAdminHint')}</p>
              )}
            </div>
          )}

          {!loading && !error && allRentalProducts.length > 0 && (
            <>
              {/* Category filter */}
              {leasingCategories.length > 0 && (
                <div className="flex flex-wrap justify-center gap-4 mb-12">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-6 py-3 rounded-full font-medium transition-all ${
                      selectedCategory === 'all'
                        ? 'bg-[#EE1C23] text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {t('common.all')}
                  </button>
                  {leasingCategories.map(category => (
                    <div key={category.id} className="relative group">
                      <button
                        onClick={() => setSelectedCategory(category.id)}
                        className={`px-6 py-3 rounded-full font-medium transition-all ${
                          selectedCategory === category.id
                            ? 'text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                        style={selectedCategory === category.id ? { backgroundColor: category.color } : {}}
                      >
                        {category.name}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Product grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product, index) => (
                  <div key={product.id} className="relative">
                    <ProductCard
                      product={product}
                      index={index}
                      isAdmin={isAdmin}
                      onEdit={handleEditClick}
                      onDelete={() => handleRemoveFromLeasing(product.id)}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      language={currentLang}
                      categories={allCategories}
                      onShowDetails={handleShowDetails}
                      sectionColor="red"
                      onRentalRequest={handleRentalRequest}
                      deleteConfirmText={t('leasing.confirmRemoveFromLeasing')}
                    />
                    {/* Leasing category assignment chips (admin only) */}
                    {isAdmin && leasingCategories.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {leasingCategories.map(lc => {
                          const active = (assignments[product.id] || []).includes(lc.id);
                          return (
                            <button
                              key={lc.id}
                              onClick={() => handleAssignToCategory(product.id, lc.id)}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition-all ${
                                active ? 'text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                              }`}
                              style={active ? { backgroundColor: lc.color } : {}}
                            >
                              {lc.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Product Details Modal */}
      {showProductDetails && selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closeProductDetails}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-900 border border-red-500/30 rounded-2xl p-6 w-[90vw] max-w-2xl max-h-[90vh] overflow-y-auto relative"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white font-orbitron">
                {selectedProduct.name[currentLang] || selectedProduct.name.cs}
              </h3>
              <button onClick={closeProductDetails} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {!showRentalFormInline && !rentalSubmitted && (
              <>
                <div className="w-full h-64 mb-6">
                  <ProductImageGallery product={selectedProduct} />
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.description')}:</h4>
                    <p className="text-gray-400">
                      {selectedProduct.description[currentLang] || selectedProduct.description.cs || t('leasing.descriptionNotAvailable')}
                    </p>
                  </div>

                  {selectedProduct.manual && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.manual')}:</h4>
                      <a
                        href={selectedProduct.manual}
                        download
                        className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        <span>{t('shop.downloadManual')}</span>
                      </a>
                    </div>
                  )}

                  {selectedProduct.videoTutorial && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-300 mb-2 flex items-center gap-2">
                        <Video className="w-5 h-5 text-[#EE1C23]" /> {t('shop.videoTutorial')}:
                      </h4>
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                          src={selectedProduct.videoTutorial}
                          title={t('leasing.productVideo')}
                          className="absolute top-0 left-0 w-full h-full rounded-xl"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}

                  {selectedProduct.isRental && (
                    <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 space-y-3">
                      <h3 className="text-red-400 font-semibold mb-2">{t('leasing.priceList')}</h3>
                      <div className="flex justify-between text-gray-300">
                        <span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-[#EE1C23]" /> {t('leasing.monthlyFlat')}:</span>
                        <span className="font-semibold text-white">{formatPrice(selectedProduct.rentalPriceMonthly || 0, currentLang)} {t('leasing.perMonth')}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#EE1C23]" /> {t('leasing.insurancePerMonth')}:</span>
                        <span className="font-semibold text-white">{formatPrice(selectedProduct.rentalInsuranceMonthly || 0, currentLang)} {t('leasing.perMonth')}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#EE1C23]" /> {t('leasing.operatingHour')}:</span>
                        <span className="font-semibold text-white">{formatPrice(selectedProduct.rentalHourlyRate || 0, currentLang)} {t('leasing.perHour')}</span>
                      </div>
                    </div>
                  )}

                  {selectedProduct.categories && selectedProduct.categories.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.categories')}:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.categories.map((categoryId) => {
                          const category = allCategories.find(c => c.id === categoryId);
                          return category ? (
                            <span
                              key={categoryId}
                              className="px-3 py-1 rounded-full text-sm font-bold text-white"
                              style={{ backgroundColor: category.color }}
                            >
                              {category.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

              <div className="pt-4">
                {!showRentalFormInline && !rentalSubmitted ? (
                  <button
                    onClick={() => handleRentalRequest(selectedProduct)}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#EE1C23] to-red-600 hover:from-red-600 hover:to-[#EE1C23] text-white py-3 rounded-lg transition-all font-semibold"
                  >
                    <Send className="w-5 h-5" />
                    <span>{t('leasing.interestedInRental')}</span>
                  </button>
                ) : rentalSubmitted ? (
                  <div className="text-center py-6">
                    <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">{t('leasing.requestSent')}</h3>
                    <p className="text-gray-400 mb-6 text-sm">
                      {t('leasing.requestSentDesc', { name: selectedProduct.name[currentLang] || selectedProduct.name.cs })}
                    </p>
                    <button
                      onClick={closeProductDetails}
                      className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors font-semibold"
                    >
                      {t('leasing.close')}
                    </button>
                  </div>
                ) : (
                  <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white">{t('leasing.rentalRequest')}</h3>
                      <button
                        onClick={backToProductDetails}
                        className="text-gray-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {t('leasing.backToDetail')}
                      </button>
                    </div>
                    <p className="text-gray-400 mb-4 text-sm">
                      {t('leasing.machine')}: <span className="text-red-400 font-semibold">{selectedProduct.name[currentLang] || selectedProduct.name.cs}</span>
                    </p>

                    {selectedProduct.isRental && (
                      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-3 mb-4 space-y-2 text-sm">
                        <div className="flex justify-between text-gray-300">
                          <span><Wallet className="w-3 h-3 inline mr-1 text-[#EE1C23]" />{t('leasing.monthlyFlat')}:</span>
                          <span className="text-white font-semibold">{formatPrice(selectedProduct.rentalPriceMonthly || 0, currentLang)}</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span><Shield className="w-3 h-3 inline mr-1 text-[#EE1C23]" />{t('leasing.insurancePerMonth')}:</span>
                          <span className="text-white font-semibold">{formatPrice(selectedProduct.rentalInsuranceMonthly || 0, currentLang)}</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span><Clock className="w-3 h-3 inline mr-1 text-[#EE1C23]" />{t('leasing.operatingHour')}:</span>
                          <span className="text-white font-semibold">{formatPrice(selectedProduct.rentalHourlyRate || 0, currentLang)}</span>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmitRental} className="space-y-3">
                      <div>
                        <label className="block text-gray-300 mb-1 text-sm">{t('leasing.nameSurname')}</label>
                        <input
                          type="text"
                          required
                          value={rentalForm.name}
                          onChange={(e) => setRentalForm({ ...rentalForm, name: e.target.value })}
                          className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-gray-300 mb-1 text-sm">{t('leasing.email')}</label>
                          <input
                            type="email"
                            required
                            value={rentalForm.email}
                            onChange={(e) => setRentalForm({ ...rentalForm, email: e.target.value })}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 mb-1 text-sm">{t('leasing.phone')}</label>
                          <input
                            type="tel"
                            value={rentalForm.phone}
                            onChange={(e) => setRentalForm({ ...rentalForm, phone: e.target.value })}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-gray-300 mb-1 text-sm">{t('leasing.notes')}</label>
                        <textarea
                          rows={3}
                          value={rentalForm.notes}
                          onChange={(e) => setRentalForm({ ...rentalForm, notes: e.target.value })}
                          className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none resize-none"
                          placeholder={t('leasing.notesPlaceholder')}
                        />
                      </div>
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={rentalForm.gdprConsent}
                          onChange={(e) => setRentalForm({ ...rentalForm, gdprConsent: e.target.checked })}
                          required
                          className="mt-1 w-4 h-4 text-[#EE1C23] bg-gray-800 border-gray-600 rounded focus:ring-[#EE1C23] focus:ring-2"
                        />
                        <label className="text-gray-300 text-sm">
                          {t('leasing.consentText')} <span className="text-red-400">*</span>
                        </label>
                      </div>
                      <button
                        type="submit"
                        disabled={!rentalForm.gdprConsent || rentalSubmitting}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#EE1C23] to-red-600 hover:from-red-600 hover:to-[#EE1C23] text-white py-3 rounded-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-5 h-5" />
                        <span>{rentalSubmitting ? t('contact.sending') : t('leasing.sendRequest')}</span>
                      </button>
                      {rentalError && (
                        <div className="mt-2 p-3 bg-red-600/20 border border-red-500/30 rounded-lg">
                          <p className="text-red-400 text-sm text-center">
                            {t('contact.errorMessage')}
                          </p>
                        </div>
                      )}
                    </form>
                  </div>
                )}
              </div>
          </motion.div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closeEditModal}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-900 border border-red-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={closeEditModal} className="absolute top-4 right-4 text-gray-400 hover:text-white z-10">
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-white mb-6 font-orbitron">{t('leasing.editProduct')}</h2>
            <ProductEditForm
              product={editingProduct}
              categories={allCategories}
              onSave={handleEditProduct}
              onCancel={closeEditModal}
              themeColor="[#EE1C23]"
              leasingCategories={leasingCategories.map(lc => ({ id: lc.id, name: lc.name, color: lc.color }) as LeasingCategoryInfo)}
              leasingCategoryIds={assignments[editingProduct.id] || []}
            />
          </motion.div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => {
            setShowAddCategoryModal(false);
            document.body.style.overflow = 'unset';
            window.dispatchEvent(new Event('modalClose'));
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-900 border border-red-500/30 rounded-2xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => {
              setShowAddCategoryModal(false);
              document.body.style.overflow = 'unset';
              window.dispatchEvent(new Event('modalClose'));
            }} className="absolute top-4 right-4 text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-white mb-6 font-orbitron">{t('leasing.newLeasingCategory')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">{t('leasing.categoryName')}</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                  placeholder={t('leasing.categoryPlaceholder')}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">{t('leasing.color')}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={newCategoryColor}
                    onChange={(e) => setNewCategoryColor(e.target.value)}
                    className="w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 cursor-pointer"
                  />
                  <span className="text-gray-400 text-sm">{newCategoryColor}</span>
                </div>
              </div>
              <button
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim()}
                className="w-full bg-gradient-to-r from-[#EE1C23] to-red-600 hover:from-red-600 hover:to-[#EE1C23] text-white py-3 rounded-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('leasing.createCategory')}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Products to Leasing Modal */}
      {showAddProductModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => {
            setShowAddProductModal(false);
            document.body.style.overflow = 'unset';
            window.dispatchEvent(new Event('modalClose'));
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-900 border border-red-500/30 rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white font-orbitron">{t('leasing.addMachinesToLeasing')}</h2>
              <button onClick={() => {
                setShowAddProductModal(false);
                document.body.style.overflow = 'unset';
                window.dispatchEvent(new Event('modalClose'));
              }} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* Leasing category selector */}
              {leasingCategories.length > 0 && (
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2 text-sm">{t('leasing.assignToCategory')}</label>
                  <select
                    value={selectedLeasingCatForAdd}
                    onChange={(e) => setSelectedLeasingCatForAdd(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                  >
                    <option value="">{t('leasing.noCategory')}</option>
                    {leasingCategories.map(lc => (
                      <option key={lc.id} value={lc.id}>{lc.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={addProductSearch}
                  onChange={(e) => setAddProductSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-[#EE1C23] focus:outline-none"
                  placeholder={t('leasing.searchProduct')}
                />
              </div>

              {/* Product list */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredAddProducts.length === 0 && (
                  <p className="text-gray-500 text-center py-8">{t('leasing.noProductsAvailable')}</p>
                )}
                {filteredAddProducts.map(product => {
                  const checked = selectedProductIdsForAdd.has(product.id);
                  return (
                    <label
                      key={product.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked ? 'border-[#EE1C23] bg-[#EE1C23]/10' : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setSelectedProductIdsForAdd(prev => {
                            const next = new Set(prev);
                            if (next.has(product.id)) next.delete(product.id);
                            else next.add(product.id);
                            return next;
                          });
                        }}
                        className="w-5 h-5 accent-[#EE1C23]"
                      />
                      {product.image && (
                        <img src={product.image} alt="" className="w-10 h-10 object-cover rounded" />
                      )}
                      <span className="text-white text-sm flex-1">
                        {product.name[currentLang] || product.name.cs}
                      </span>
                      <span className="text-gray-400 text-sm">{formatPrice(product.price, i18n.language)}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 flex justify-between items-center">
              <span className="text-gray-400 text-sm">
                {selectedProductIdsForAdd.size > 0
                  ? `${t('leasing.selected')}: ${selectedProductIdsForAdd.size} ${selectedProductIdsForAdd.size === 1 ? t('leasing.product') : t('leasing.products')}`
                  : t('leasing.notSelected')}
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAddProductModal(false);
                    document.body.style.overflow = 'unset';
                    window.dispatchEvent(new Event('modalClose'));
                  }}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t('leasing.cancel')}
                </button>
                <button
                  onClick={handleConfirmAddProducts}
                  disabled={selectedProductIdsForAdd.size === 0}
                  className="px-6 py-2 bg-gradient-to-r from-[#EE1C23] to-red-600 hover:from-red-600 hover:to-[#EE1C23] text-white rounded-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('leasing.addToLeasing')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Helper: extract YouTube ID
// ============================================================

function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  /^[a-zA-Z0-9_-]{11}$/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

// ============================================================
// Leasing Video Tutorial Section
// ============================================================

function LeasingVideoTutorialSection({ videoId, expanded, onToggle }: {
  videoId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-[#EE1C23]/20 to-red-600/10 border border-[#EE1C23]/30 hover:border-[#EE1C23]/50 transition-all group"
      >
        <div className="flex items-center gap-3">
          <div className="bg-[#EE1C23]/30 rounded-lg p-2">
            <Youtube className="w-6 h-6 text-[#EE1C23]" />
          </div>
          <span className="text-white font-semibold text-base sm:text-lg">
            {t('leasing.howItWorks')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-2xl bg-black/60 border border-[#EE1C23]/20 p-4">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${videoId}`}
                  title={t('leasing.howItWorks')}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================================
// Leasing Video Tutorial Modal (admin)
// ============================================================

function LeasingVideoTutorialModal({ currentUrl, onClose, onSave, onRemove }: {
  currentUrl: string;
  onClose: () => void;
  onSave: (url: string) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(currentUrl);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(url.trim());
    } catch {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await onRemove();
    } catch {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-[#EE1C23]/30 rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{t('leasing.setVideoTutorial')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('leasing.videoTutorialUrl')}</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('leasing.videoTutorialPlaceholder')}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#EE1C23]/50 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          {currentUrl && (
            <button
              onClick={handleRemove}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 text-sm font-medium hover:bg-red-500/30 transition-all disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/10 text-gray-300 text-sm font-medium hover:bg-white/20 transition-all">
            {t('leasing.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-40"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
